import React, { useState } from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import {
  Users, FileText, Bell, Workflow, Database, ShieldCheck,
  BarChart3, BookOpen, Receipt, ClipboardCheck, ArrowRight,
  TrendingUp, PieChart, FolderOpen, Globe, Zap, Layers,
  Lock, UserCheck, Settings, CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";

const PORTAL_HIGHLIGHTS = [
  { icon: <UserCheck size={24} />, title: "Investor Onboarding", desc: "Streamlined investor and entity onboarding with guided workflows, KYC/AML support, and automated status tracking.", accent: "#4B71E8" },
  { icon: <FolderOpen size={24} />, title: "Document Repository", desc: "Centralized document storage with version control, granular permissions, investor-specific distribution, and audit logging.", accent: "#5B8DEF" },
  { icon: <Workflow size={24} />, title: "Request Management", desc: "Configurable request and approval workflows with multi-level authorization chains and automated routing.", accent: "#3A5FD9" },
  { icon: <Lock size={24} />, title: "Fund Manager Login", desc: "Secure portal access for fund managers with institutional-grade authentication, session management, and role-based authorization.", accent: "#4B71E8" },
  { icon: <Bell size={24} />, title: "Smart Notifications", desc: "Automated notifications for approvals, updates, deadlines, and critical events across all channels.", accent: "#6B9EFF" },
  { icon: <ClipboardCheck size={24} />, title: "Operations Hub", desc: "End-to-end operational process management with real-time status tracking and performance metrics.", accent: "#4B71E8" },
  { icon: <ShieldCheck size={24} />, title: "Access Control", desc: "Granular role-based permissions across all platform resources, data, and operational workflows.", accent: "#3A5FD9" },
  { icon: <BarChart3 size={24} />, title: "Reporting", desc: "Comprehensive reporting engine with customizable templates, automated scheduled delivery, and real-time analytics dashboards for stakeholders.", accent: "#5B8DEF" },
];

const GL_HIGHLIGHTS = [
  { icon: <BookOpen size={24} />, title: "General Ledger", desc: "Complete chart of accounts with multi-level hierarchy, automated posting, and real-time balance tracking.", accent: "#4B71E8" },
  { icon: <Receipt size={24} />, title: "Accounting Entries", desc: "Detailed transaction records with journal entries, reconciliation support, and automated validations.", accent: "#5B8DEF" },
  { icon: <BarChart3 size={24} />, title: "Financial Reporting", desc: "P&L, balance sheet, trial balance, lot summary, and fully customizable report generation engine.", accent: "#3A5FD9" },
  { icon: <Workflow size={24} />, title: "Fund Workflows", desc: "Automated NAV calculations, fee accruals, period-end closures, and waterfall distributions.", accent: "#6B9EFF" },
  { icon: <Database size={24} />, title: "Investment Accounting", desc: "Multi-asset class support including equities, fixed income, alternatives, and structured products.", accent: "#4B71E8" },
  { icon: <ShieldCheck size={24} />, title: "Audit Trail", desc: "Complete audit trails with version history, timestamps, and compliance reporting for every transaction.", accent: "#3A5FD9" },
];

const PORTAL_METRICS = [
  { value: "100%", label: "Automated Onboarding" },
  { value: "Real-time", label: "Status Tracking" },
  { value: "Multi-level", label: "Approval Chains" },
  { value: "AI-Assisted", label: "Workflows" },
];

const GL_METRICS = [
  { value: "Multi-fund", label: "Accounting" },
  { value: "Automated", label: "NAV Calculation" },
  { value: "Institutional", label: "Reporting" },
  { value: "Full", label: "Audit Coverage" },
];

const PORTAL_FLOW_STEPS = [
  { num: "01", title: "Onboard", desc: "Investors & entities with guided workflows" },
  { num: "02", title: "Manage", desc: "Documents, approvals, and communications" },
  { num: "03", title: "Monitor", desc: "Operations with real-time dashboards" },
  { num: "04", title: "Report", desc: "Deliver insights to stakeholders" },
];

const GL_FLOW_STEPS = [
  { num: "01", title: "Record", desc: "Transactions and journal entries" },
  { num: "02", title: "Process", desc: "NAV calculations and fee accruals" },
  { num: "03", title: "Reconcile", desc: "Accounts with automated matching" },
  { num: "04", title: "Report", desc: "Financial statements and analytics" },
];

const PORTAL_PREVIEWS = [
  { src: "/screenshots/fm-dashboard.png", title: "Fund Manager Dashboard", desc: "Fund overview with AUM tracking, investor activity, and operational monitoring." },
  { src: "/screenshots/investor-dashboard-full.png", title: "Investor Dashboard", desc: "Personalized portfolio view with returns, income charts, and quick actions." },
];

const GL_PREVIEWS = [
  { src: "/screenshots/analytics-dashboard.png", title: "GL Dashboard", desc: "Comprehensive fund analytics with AUM tracking, investor metrics, and operational overview." },
  { src: "/screenshots/gl-reports.png", title: "GL Reports", desc: "Comprehensive reports management with Lot Summary, RPNL, Balance Sheet, Trial Balance, and P&L." },
];

function ModuleHero({ info, isGL }) {
  return (
    <div className="w-mod-hero" data-testid="module-hero">
      <div className="w-mod-hero-content w-reveal">
        <div className="w-mod-hero-badge">{info.sub}</div>
        <h2 className="w-mod-hero-title">{info.name}</h2>
        <p className="w-mod-hero-desc">{info.desc}</p>
      </div>
      <div className="w-mod-hero-visual w-reveal-scale w-delay-1">
        <div className="w-mod-hero-visual-grid">
          {(isGL ? [
            { icon: <BookOpen size={20} />, label: "Ledger" },
            { icon: <BarChart3 size={20} />, label: "Reports" },
            { icon: <Receipt size={20} />, label: "Entries" },
            { icon: <TrendingUp size={20} />, label: "Analytics" },
          ] : [
            { icon: <Users size={20} />, label: "Investors" },
            { icon: <FolderOpen size={20} />, label: "Documents" },
            { icon: <Workflow size={20} />, label: "Workflows" },
            { icon: <PieChart size={20} />, label: "Dashboard" },
          ]).map((item, i) => (
            <div key={i} className="w-mod-hero-visual-card" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="w-mod-hero-visual-icon">{item.icon}</div>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="w-mod-hero-visual-ring" />
      </div>
    </div>
  );
}

function ModuleFlow({ steps }) {
  return (
    <div className="w-mod-flow w-reveal w-delay-1" data-testid="module-flow">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div className="w-mod-flow-step">
            <div className="w-mod-flow-num">{step.num}</div>
            <h4 className="w-mod-flow-title">{step.title}</h4>
            <p className="w-mod-flow-desc">{step.desc}</p>
          </div>
          {i < steps.length - 1 && <div className="w-mod-flow-connector"><ArrowRight size={16} /></div>}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ModulesPage() {
  const [tab, setTab] = useState("portal");
  useScrollReveal();

  const highlights = tab === "gl" ? GL_HIGHLIGHTS : PORTAL_HIGHLIGHTS;
  const metrics = tab === "gl" ? GL_METRICS : PORTAL_METRICS;
  const flowSteps = tab === "gl" ? GL_FLOW_STEPS : PORTAL_FLOW_STEPS;
  const previews = tab === "gl" ? GL_PREVIEWS : PORTAL_PREVIEWS;
  const info = tab === "gl"
    ? { num: "02", name: "Tychi GL", sub: "Accounting Engine", desc: "The core financial accounting infrastructure. A comprehensive engine handling general ledger management, investment accounting, fund accounting workflows, transaction tracking, financial reporting, and audit-ready records." }
    : { num: "01", name: "Tychi Portal", sub: "Operations Platform", desc: "The operational and workflow platform handling organization onboarding, document management, request and approval workflows, client interactions, data management, notification systems, and operational monitoring." };

  return (
    <WebsiteLayout>
      {/* Banner */}
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="modules-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Platform Modules</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Two Integrated <strong>Modules</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 560 }}>
            Tychi operates through two core modules designed for seamless integration
            and institutional-grade financial operations.
          </p>
        </section>
      </div>

      {/* Tabs */}
      <section className="w-section" style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
        <div className="w-tabs w-reveal" data-testid="module-tabs" style={{ marginBottom: "0" }}>
          <button className={`w-tab ${tab === "portal" ? "active" : ""}`}
            onClick={() => setTab("portal")} data-testid="tab-portal">Tychi Portal</button>
          <button className={`w-tab ${tab === "gl" ? "active" : ""}`}
            onClick={() => setTab("gl")} data-testid="tab-gl">Tychi GL</button>
        </div>
      </section>

      {/* Module Hero Section */}
      <section className="w-mod-section-dark" key={tab + "-hero"}>
        <div className="w-section" style={{ paddingTop: "4rem", paddingBottom: "4rem" }}>
          <ModuleHero info={info} isGL={tab === "gl"} />
        </div>
      </section>

      {/* Metrics Bar */}
      <section className="w-section" style={{ paddingTop: "3rem", paddingBottom: "0" }}>
        <div className="w-mod-metrics w-reveal" data-testid="module-metrics">
          {metrics.map((m, i) => (
            <div key={`${tab}-m-${i}`} className="w-mod-metric">
              <div className="w-mod-metric-value">{m.value}</div>
              <div className="w-mod-metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="w-section" style={{ paddingBottom: "3rem" }}>
        <div className="w-reveal" style={{ marginBottom: "2.5rem" }}>
          <div className="w-label">How It Works</div>
          <h3 className="w-title" style={{ fontSize: "1.75rem" }}>
            Streamlined <strong>Workflow</strong>
          </h3>
        </div>
        <ModuleFlow steps={flowSteps} />
      </section>

      {/* Platform Preview */}
      <section className="w-section" style={{ paddingTop: "0", paddingBottom: "3rem" }}>
        <div className="w-reveal" style={{ marginBottom: "2.5rem" }}>
          <div className="w-label">Platform Preview</div>
          <h3 className="w-title" style={{ fontSize: "1.75rem" }}>
            See It in <strong>Action</strong>
          </h3>
        </div>
        <div className="w-mod-previews w-reveal w-delay-1" data-testid="module-previews">
          {previews.map((p, i) => (
            <div key={`${tab}-p-${i}`} className="w-mod-preview-card" data-testid={`module-preview-${i}`}>
              <div className="w-screenshot-modern">
                <img src={p.src} alt={p.title} loading="lazy" />
              </div>
              <div className="w-mod-preview-info">
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="w-section-full w-section-surface">
        <div className="w-section" style={{ paddingTop: "5rem", paddingBottom: "5rem" }}>
          <div className="w-reveal" style={{ marginBottom: "3rem" }}>
            <div className="w-label">{tab === "gl" ? "GL" : "Portal"} Capabilities</div>
            <h3 className="w-title" style={{ fontSize: "1.75rem" }}>
              Key <strong>Features</strong>
            </h3>
            <p className="w-subtitle">
              {tab === "gl"
                ? "Institutional-grade accounting tools built for modern fund operations."
                : "Everything you need to manage investor operations at scale."}
            </p>
          </div>
          <div className="w-mod-highlights" data-testid="module-highlights">
            {highlights.map((h, i) => (
              <div key={`${tab}-h-${i}`} className={`w-mod-highlight w-reveal w-delay-${(i % 3) + 1}`} data-testid={`module-highlight-${i}`}>
                <div className="w-mod-highlight-top">
                  <div className="w-mod-highlight-icon">{h.icon}</div>
                  <h4 className="w-mod-highlight-title">{h.title}</h4>
                </div>
                <p className="w-mod-highlight-desc">{h.desc}</p>
                <div className="w-mod-highlight-bottom">
                  <span className="w-mod-highlight-tag"><CheckCircle2 size={12} /> Available Now</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="w-section">
        <div className="w-split">
          <div className="w-reveal-left">
            <div className="w-label">Why {info.name}</div>
            <h3 className="w-title" style={{ fontSize: "1.75rem" }}>
              Built for <strong>Scale</strong>
            </h3>
            <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>
              {tab === "gl"
                ? "Tychi GL handles the complexity of multi-fund accounting so your team can focus on generating returns. From automated NAV calculations to institutional-grade reporting."
                : "Tychi Portal eliminates operational friction with intelligent workflows and real-time visibility. Every process is tracked, auditable, and designed for institutional standards."}
            </p>
            <ul className="w-features">
              {(tab === "gl" ? [
                "Handles multi-fund, multi-currency accounting",
                "Automated reconciliation and period-end workflows",
                "Real-time reporting with drill-down capability",
                "Complete audit trail for regulatory compliance",
                "Scales with your AUM without added complexity"
              ] : [
                "Reduces onboarding time by up to 70%",
                "AI-assisted approval routing and tracking",
                "Real-time visibility into all operations",
                "Institutional-grade security and access controls",
                "Scales from single fund to fund-of-funds"
              ]).map((f, i) => (
                <li key={i}><span className="check-icon"><CheckCircle2 size={12} /></span> {f}</li>
              ))}
            </ul>
          </div>
          <div className="w-reveal-right w-delay-2">
            <div className="w-mod-why-visual">
              <div className="w-mod-why-card">
                <Globe size={28} style={{ color: "var(--w-blue)" }} />
                <h4>Global Ready</h4>
                <p>Multi-currency, multi-jurisdiction support</p>
              </div>
              <div className="w-mod-why-card">
                <Zap size={28} style={{ color: "var(--w-blue)" }} />
                <h4>Lightning Fast</h4>
                <p>Real-time processing and reporting</p>
              </div>
              <div className="w-mod-why-card">
                <Lock size={28} style={{ color: "var(--w-blue)" }} />
                <h4>Bank-Grade Security</h4>
                <p>Encryption at rest and in transit</p>
              </div>
              <div className="w-mod-why-card">
                <Layers size={28} style={{ color: "var(--w-blue)" }} />
                <h4>Seamless Integration</h4>
                <p>Works with your existing infrastructure</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-cta-section">
        <div className="w-reveal">
          <h2 className="w-title" style={{ maxWidth: 500, margin: "0 auto 1rem" }}>
            See {info.name} in <strong>Action</strong>
          </h2>
          <p className="w-subtitle" style={{ margin: "0 auto 2rem", maxWidth: 400 }}>
            Schedule a personalized walkthrough with our team and discover how Tychi transforms your operations.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/contact" className="w-btn w-btn-primary" data-testid="modules-cta-demo">
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/platform" className="w-btn w-btn-ghost" data-testid="modules-cta-platform">
              Explore Platform
            </Link>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
